import { createFeature, createReducer, on } from '@ngrx/store';
import { HubNotification, UserHubNotification } from '@models/notification.model';
import { NotificationsAdminActions, NotificationsUserActions } from '@store/actions/notifications/notifications.actions';

export const notificationsFeatureKey = 'notifications';

export interface State {
  notifications: HubNotification[];
  userNotifications: UserHubNotification[];
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  showInactive: boolean;
  welcomeModalShown: boolean;
  error: string | null;
}

export const initialState: State = {
  notifications: [],
  userNotifications: [],
  loading: false,
  saving: false,
  deleting: false,
  showInactive: false,
  welcomeModalShown: false,
  error: null,
};

export const reducer = createReducer(
  initialState,

  on(NotificationsAdminActions.loadNotifications, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(NotificationsAdminActions.loadNotificationsSuccess, (state, { notifications }) => ({
    ...state,
    notifications,
    loading: false,
    error: null,
  })),
  on(NotificationsAdminActions.loadNotificationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(NotificationsAdminActions.createNotification, (state) => ({
    ...state,
    saving: true,
    error: null,
  })),
  on(NotificationsAdminActions.createNotificationSuccess, (state) => ({
    ...state,
    saving: false,
    error: null,
  })),
  on(NotificationsAdminActions.createNotificationFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
  })),

  on(NotificationsAdminActions.updateNotification, (state) => ({
    ...state,
    saving: true,
    error: null,
  })),
  on(NotificationsAdminActions.updateNotificationSuccess, (state, { notification }) => ({
    ...state,
    saving: false,
    notifications: state.notifications.map((n) => (n.id === notification.id ? notification : n)),
    error: null,
  })),
  on(NotificationsAdminActions.updateNotificationFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
  })),

  on(NotificationsAdminActions.deleteNotification, (state) => ({
    ...state,
    deleting: true,
    error: null,
  })),
  on(NotificationsAdminActions.deleteNotificationSuccess, (state, { id }) => ({
    ...state,
    notifications: state.notifications.map((n) =>
      n.id === id ? { ...n, isActive: false } : n
    ),
    deleting: false,
    error: null,
  })),
  on(NotificationsAdminActions.deleteNotificationFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    error,
  })),

  on(NotificationsAdminActions.reactivateNotification, (state) => ({
    ...state,
    saving: true,
    error: null,
  })),
  on(NotificationsAdminActions.reactivateNotificationSuccess, (state, { id }) => ({
    ...state,
    notifications: state.notifications.map((n) =>
      n.id === id ? { ...n, isActive: true } : n
    ),
    saving: false,
    error: null,
  })),
  on(NotificationsAdminActions.reactivateNotificationFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
  })),

  on(NotificationsAdminActions.setShowInactive, (state, { showInactive }) => ({
    ...state,
    showInactive,
  })),

  on(NotificationsUserActions.loadUserNotifications, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(NotificationsUserActions.loadUserNotificationsSuccess, (state, { notifications }) => ({
    ...state,
    userNotifications: notifications,
    loading: false,
    error: null,
  })),
  on(NotificationsUserActions.loadUserNotificationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(NotificationsUserActions.markNotificationReadSuccess, (state, { id }) => ({
    ...state,
    userNotifications: state.userNotifications.map((n) =>
      n.id === id ? { ...n, isRead: true } : n
    ),
  })),

  on(NotificationsUserActions.markNotificationUnreadSuccess, (state, { id }) => ({
    ...state,
    userNotifications: state.userNotifications.map((n) =>
      n.id === id ? { ...n, isRead: false, readDate: undefined } : n
    ),
  })),

  on(NotificationsUserActions.welcomeModalShown, (state) => ({
    ...state,
    welcomeModalShown: true,
  }))
);

export const notificationsFeature = createFeature({
  name: notificationsFeatureKey,
  reducer,
});
